import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import MusicShell from "../fanHome/MusicShell";
import { ACCENT } from "../artistStudio/shared";
import { INK, MUTED } from "../fanHome/libraryParts";
import { fanMembershipApi, money, formatDate, apiErrorMessage } from "./fanMembershipApi";
import { receiptLink } from "../receipts/receiptApi";

const SETTINGS_ROWS = [
  { to: "/fan/preferences", label: "Notification & privacy preferences", help: "Emails and notices, public profile, who can message you" },
  { to: "/fan/payment-methods", label: "Payment methods", help: "Cards saved for automatic licence renewal" },
];

// My Plan: the fan's current plan, Change Plan / Cancel Subscription, and the membership billing history. A membership is paid
// one period at a time (no automatic renewal), so the card says when access runs until rather than a renewal date.
export default function MyPlan() {
  const [state, setState] = useState({ loading: true, sub: null, history: [], error: "" });
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const [current, history] = await Promise.all([fanMembershipApi.current(), fanMembershipApi.history()]);
      setState({ loading: false, sub: current?.subscription || null, history: history?.items || [], error: "" });
    } catch (e) {
      setState({ loading: false, sub: null, history: [], error: apiErrorMessage(e, "Your plan could not be loaded. Please try again.") });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    setNotice("");
    try {
      const result = await fanMembershipApi.cancel();
      setState((s) => ({ ...s, sub: result.subscription }));
      setConfirming(false);
      setNotice("Your subscription is cancelled. You keep your benefits until the end of the period you paid for.");
    } catch (e) {
      setNotice(apiErrorMessage(e, "Your subscription could not be cancelled. Please try again."));
    } finally {
      setCancelling(false);
    }
  };

  const { loading, sub, history, error } = state;
  const cancelled = sub?.status === "cancelled";

  return (
    <MusicShell>
      <h1 className="text-[28px] font-bold leading-tight" style={{ color: INK }}>My Plan</h1>
      {loading && <div className="flex justify-center py-20" role="status" aria-label="Loading plan"><Loading /></div>}
      {!loading && error && (
        <div className="py-12 max-w-md">
          <p className="text-gray-700 mb-4" role="alert">{error}</p>
          <button type="button" onClick={load} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
        </div>
      )}
      {!loading && !error && (
        <div className="mt-6 max-w-3xl">
          {sub ? (
            <section aria-label="Current plan" className="rounded-3xl border border-gray-200 bg-[#f4f7f4] p-7">
              <h2 className="text-2xl font-bold" style={{ color: INK }}>{sub.plan_name}</h2>
              <p className="mt-1 text-[15px]" style={{ color: MUTED }}>
                {sub.billing_label} · {cancelled ? "Access ends" : "Active until"} {formatDate(sub.current_period_end)}
              </p>
              {notice && <p className="mt-3 text-sm text-gray-700" role="status">{notice}</p>}
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/fan-membership" className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-[15px] font-semibold hover:bg-gray-50" style={{ color: INK }}>Change Plan</Link>
                {!cancelled && (
                  <button type="button" onClick={() => setConfirming(true)} className="rounded-full border border-red-500 bg-white px-6 py-2.5 text-[15px] font-semibold text-red-600 hover:bg-red-50">Cancel Subscription</button>
                )}
              </div>
            </section>
          ) : (
            <section aria-label="No plan" className="rounded-3xl border border-gray-200 bg-[#f4f7f4] p-7">
              <h2 className="text-2xl font-bold" style={{ color: INK }}>No active plan</h2>
              <p className="mt-1 text-[15px]" style={{ color: MUTED }}>Choose a plan to get unlimited streaming across Pipaan.</p>
              <Link to="/fan-membership" className="mt-5 inline-block rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Choose your plan</Link>
            </section>
          )}

          <section aria-label="Account settings" className="mt-8">
            <ul>
              {SETTINGS_ROWS.map((r) => (
                <li key={r.to} className="border-b border-gray-200">
                  <Link to={r.to} className="flex items-center justify-between gap-4 py-4 text-[15px] hover:bg-gray-50">
                    <span><span className="font-semibold" style={{ color: INK }}>{r.label}</span><span className="block text-sm" style={{ color: MUTED }}>{r.help}</span></span>
                    <span aria-hidden="true" style={{ color: MUTED }}>›</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section aria-label="Billing history" className="mt-8">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-semibold" style={{ color: INK }}>Billing history</h2>
              <Link to="/fan/purchases" className="text-sm font-semibold hover:underline" style={{ color: ACCENT }}>View full purchase history →</Link>
            </div>
            {history.length === 0 ? (
              <p className="mt-4 text-sm" style={{ color: MUTED }}>No payments yet.</p>
            ) : (
              <ul className="mt-3">
                {history.slice(0, 5).map((h) => (
                  <li key={h.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-gray-200 py-4 text-[15px]">
                    <span style={{ color: INK }}>{formatDate(h.date)}</span>
                    <Link to={receiptLink("membership", h.id)} className="text-gray-500 hover:underline">{h.plan_name} — {h.billing_period}</Link>
                    <span className="min-w-[72px] text-right font-semibold" style={{ color: INK }}>{money(h.total_amount, h.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
      {confirming && sub && !cancelled && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="alertdialog" aria-modal="true" aria-label="Cancel subscription">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-xl">
            <h2 className="text-xl font-bold" style={{ color: INK }}>Cancel subscription?</h2>
            <p className="mt-3 text-sm" style={{ color: MUTED }}>
              You'll keep streaming access until {formatDate(sub.current_period_end)}, then your account moves to the free tier. Any licences you've already bought stay yours.
            </p>
            <button type="button" onClick={cancel} disabled={cancelling} className="mt-5 w-full rounded-full bg-[#7a1c1c] px-5 py-3 text-[15px] font-semibold text-white disabled:opacity-60">{cancelling ? "Cancelling…" : "Cancel Subscription"}</button>
            <button type="button" onClick={() => setConfirming(false)} disabled={cancelling} className="mt-3 w-full rounded-full border border-gray-200 px-5 py-3 text-[15px] font-semibold" style={{ color: INK }}>Keep Subscription</button>
          </div>
        </div>
      )}
    </MusicShell>
  );
}
